export type HousingBurdenViewId =
  | 'threshold_35plus'
  | 'renter_distribution'
  | 'owner_distribution'
  | 'percentile';

export type HousingBurdenMetricsInput = {
  comparisonYear: number | null;
  latestRenterYear: number | null;
  latestOwnerYear: number | null;
  renter35Plus: number | null;
  owner35Plus: number | null;
  renterPercentile: number | null;
  ownerPercentile: number | null;
  largestRenterBurdenBand?: string | null;
  largestRenterBurdenShare?: number | null;
  ownerDistributionSentence?: string;
  percentileContextSentence?: string;
};

export type HousingBurdenNarrativeFacts = {
  locationName: string;
  viewId: HousingBurdenViewId;
  comparisonYear: number | null;
  latestRenterYear: number | null;
  latestOwnerYear: number | null;
  renter35Plus: number | null;
  owner35Plus: number | null;
  renterPercentile: number | null;
  ownerPercentile: number | null;
  comparisonSentence: string;
  trendSentence: string;
  renter35Sentence: string;
  owner35Sentence: string;
  ownerDistributionSentence: string;
  percentileContextSentence: string;
  largestRenterBurdenBand: string;
  largestRenterBurdenShare: number | null;
};

export function housingBurdenTabToViewId(
  tab: '35plus' | 'renter' | 'owner' | 'percentile'
): HousingBurdenViewId {
  if (tab === '35plus') return 'threshold_35plus';
  if (tab === 'renter') return 'renter_distribution';
  if (tab === 'owner') return 'owner_distribution';
  return 'percentile';
}

export function buildHousingBurdenNarrativeFacts(params: {
  locationName: string;
  viewId: HousingBurdenViewId;
} & HousingBurdenMetricsInput): HousingBurdenNarrativeFacts {
  const comparisonSentence =
    params.comparisonYear != null &&
    params.renter35Plus != null &&
    params.owner35Plus != null
      ? ''
      : params.viewId === 'threshold_35plus'
        ? ' Renter and owner values are shown for the latest year available for each tenure when a common comparison year is not available.'
        : '';

  return {
    locationName: params.locationName,
    viewId: params.viewId,
    comparisonYear: params.comparisonYear,
    latestRenterYear: params.latestRenterYear,
    latestOwnerYear: params.latestOwnerYear,
    renter35Plus: params.renter35Plus,
    owner35Plus: params.owner35Plus,
    renterPercentile: params.renterPercentile,
    ownerPercentile: params.ownerPercentile,
    comparisonSentence,
    trendSentence: '',
    renter35Sentence:
      params.renter35Plus != null && params.latestRenterYear != null
        ? ` ${params.renter35Plus.toFixed(1)}% of the displayed renter universe met the 35%-or-more category in ${params.latestRenterYear}.`
        : '',
    owner35Sentence:
      params.owner35Plus != null && params.latestOwnerYear != null
        ? ` ${params.owner35Plus.toFixed(1)}% of the displayed owner universe met the 35%-or-more category in ${params.latestOwnerYear}.`
        : '',
    ownerDistributionSentence: params.ownerDistributionSentence ?? '',
    percentileContextSentence: params.percentileContextSentence ?? '',
    largestRenterBurdenBand: params.largestRenterBurdenBand ?? '—',
    largestRenterBurdenShare: params.largestRenterBurdenShare ?? null,
  };
}
